/* Hook cambia code por token en Mercado Libre

Autor: Benjamín León
Fecha: 2025-03-25
*/
import { useState } from 'react'

import { customPost } from '../utils/customFetch.ts'

import { makeUrlMeliExchangeCode, makeUrlMeliStart, makeUrlMeliTokenRefresh } from './connectionData.tsx'
import { useAuth } from './useAuth.tsx'

const useMeliLogin = () => {
  const [errorMeliStart, setErrorMeliStart] = useState<Error | null>(null)
  const [errorMeliExchangeCode, setErrorMeliExchangeCode] = useState<Error | null>(null)
  const [errorMeliTokenRefresh, setErrorMeliTokenRefresh] = useState<Error | null>(null)

  const [loading, setLoading] = useState<boolean>(false)
  const { userData } = useAuth()

  const meliStart = async () => {
    setLoading(true)

    // Try to get profile from userData, fallback to sessionStorage
    let profileId = userData?.profile?._id

    // If profile is missing from userData, try to get it from sessionStorage
    if (!profileId) {
      const storedProfileId = sessionStorage.getItem('meliProfileId')

      if (!storedProfileId) {
        setErrorMeliStart(new Error('User profile data is missing. Please log in again.'))
        setLoading(false)

        return null
      }

      profileId = storedProfileId

      console.log('Using profile ID from sessionStorage:', profileId)
    }

    try {
      // Use profile ID directly as state parameter (no JSON wrapper)
      // This avoids special characters that can cause issues with MercadoLibre's auth server
      const stateParam = profileId

      // Nos fue bien, entonces vamos con el code_change
      // No podemos hacerle POST en esta etapa
      const redirectUri = window.location.origin + '/meli/redirected'

      window.location.href = makeUrlMeliStart(stateParam, redirectUri)

      setErrorMeliStart(null)

      return 'OK'
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorMeliStart(err)
      } else {
        setErrorMeliStart(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoading(false)
    }
  }

  const meliExchangeCode = async (code: string, profileIdOverride?: string) => {
    setLoading(true)

    // Use provided profileIdOverride first, then try userData, then fallback to sessionStorage
    let profileId = profileIdOverride || userData?.profile?._id

    // If profile is still missing, try to get it from sessionStorage
    if (!profileId) {
      const storedProfileId = sessionStorage.getItem('meliProfileId')

      if (!storedProfileId) {
        setErrorMeliExchangeCode(new Error('User profile data is missing. Please log in again.'))
        setLoading(false)

        return null
      }
      profileId = storedProfileId
      console.log('Using profile ID from sessionStorage for code exchange:', profileId)
    }

    const apiParams = { profile: profileId, code: code, redirect_uri: window.location.origin + '/meli/redirected' }

    try {
      const apiUrl = makeUrlMeliExchangeCode()
      const resp = await customPost<{
        access_token: string
        refresh_token: string
        expires_at: string
      }>(apiUrl, apiParams)

      setErrorMeliExchangeCode(null)

      return resp
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorMeliExchangeCode(err)
      } else {
        setErrorMeliExchangeCode(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoading(false)
    }
  }

  const meliTokenRefresh = async (profile: string) => {
    setLoading(true)

    const apiParams = { id: profile }

    try {
      const apiUrl = makeUrlMeliTokenRefresh()
      await customPost(apiUrl, apiParams)

      setErrorMeliTokenRefresh(null)

      return 'OK'
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorMeliTokenRefresh(err)
      } else {
        setErrorMeliTokenRefresh(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoading(false)
    }
  }

  // Exponemos estas variables al exterior
  return {
    loading,
    meliExchangeCode,
    errorMeliExchangeCode,
    meliStart,
    errorMeliStart,
    meliTokenRefresh,
    errorMeliTokenRefresh,
  }
}

export default useMeliLogin
